import styled from '@emotion/styled';
import { font, mediaQueries, palette, remCalc, sizeMap } from '../../css';

const StyledProgramWrapper = styled('h3')(() => ({
    flex: 1,
    fontFamily: font.family.body,
    fontSize: font.size.xs,
    marginBottom: 0,
}));

const StyledProgramButton = styled('a')(() => ({
    minWidth: 0,
    marginTop: remCalc(3),
    padding: remCalc(2, 20),
}));

const StyledProgramFigure = styled('div')(() => ({
    position: 'relative',
    minWidth: remCalc(80),
    marginRight: remCalc(10),

    [mediaQueries.medium]: {
        minWidth: remCalc(180),
        marginRight: remCalc(15),
        '& *': {
            height: '113px',
        },
    },
}));

const StyledProgramDescription = styled('div')(
    () => ({
        display: 'none',
        position: 'absolute',

        [mediaQueries.large]: {
            display: 'block',
            height: remCalc(41),
            left: remCalc(247),
            right: remCalc(52),
            bottom: remCalc(20),
            overflow: 'hidden',
            fontSize: font.size.s,
            fontWeight: font.weight.thin,
            lineHeight: font.lineHeight.s,
            color: 'transparent',
        },
    }),
    ({ moveUp }) =>
        moveUp && {
            [mediaQueries.large]: {
                bottom: remCalc(30),
            },
        },
);

const colorDescriptionOnHoverFocus = {
    [StyledProgramDescription]: {
        [mediaQueries.large]: {
            color: palette('white'),
            transition: 'color .5s linear',
        },
    },
};

const StyledProgram = styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    marginBottom: 0,
    position: 'relative',

    '&:hover': colorDescriptionOnHoverFocus,
}));

const stylingProgramLink = (component) =>
    styled(component)(
        () => ({
            display: 'flex',
            flex: 1,
            padding: remCalc(15),
            border: `${remCalc(2)} solid transparent`,
            borderTopColor: 'rgba(255, 255, 255, .15)',
            borderTopWidth: remCalc(1),

            [mediaQueries.medium]: {
                padding: remCalc(20, 50),
            },

            '&:hover +': colorDescriptionOnHoverFocus,
        }),
        ({ mouseFocus }) =>
            !mouseFocus
                ? {
                      '&:focus': {
                          borderColor: palette('white'),
                          borderTopWidth: remCalc(2),
                          transition: 'border-color .3s linear',
                      },
                      '&:focus +': colorDescriptionOnHoverFocus,
                  }
                : {},
    );

const StyledProgramHeader = styled('span')(() => ({
    display: 'flex',
    fontSize: remCalc(sizeMap.s * 0.9),
    lineHeight: remCalc(sizeMap.s * 0.9),
    fontWeight: font.weight.medium,
    color: palette('white'),

    [mediaQueries.medium]: {
        fontSize: remCalc(sizeMap.l * 0.9),
        lineHeight: remCalc(sizeMap.l * 0.9),
    },

    '.arte-icon': {
        width: remCalc(sizeMap.xxs),
        height: remCalc(sizeMap.xxs),

        [mediaQueries.medium]: {
            width: remCalc(sizeMap.s),
            height: remCalc(sizeMap.s),
        },
    },
}));

const StyledProgramHour = styled('span')(null, ({ showDate }) =>
    showDate
        ? {
              paddingLeft: remCalc(5),
              borderLeft: '1px solid',

              [mediaQueries.medium]: {
                  paddingLeft: remCalc(15),
                  textTransform: 'none',
              },
          }
        : null,
);

const StyledProgramDate = styled('span')(() => ({
    display: 'inline-block',
    paddingRight: remCalc(5),
    textTransform: 'uppercase',

    [mediaQueries.medium]: {
        paddingRight: remCalc(15),
        textTransform: 'none',
    },
}));

const StyledProgramPlay = styled('span')(() => ({
    display: 'inline-block',
    marginLeft: remCalc(10),

    [mediaQueries.medium]: {
        marginLeft: remCalc(20),
    },
}));

const StyledProgramLive = styled('span')(() => ({
    display: 'inline-block',
    position: 'relative',
    marginLeft: remCalc(8),
    fontWeight: font.weight.bold,
    textTransform: 'uppercase',
    color: palette('arte'),

    [mediaQueries.medium]: {
        marginLeft: remCalc(20),
        paddingLeft: remCalc(18),

        '::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: remCalc(10),
            width: remCalc(10),
            height: remCalc(10),
            borderRadius: '50%',
            backgroundColor: palette('arte'),
            fontSize: font.size.xl,
        },
    },
}));

const StyledProgramTitlesWrapper = styled('span')(
    {
        display: 'block',
    },
    ({ withPlaceholder }) =>
        withPlaceholder && {
            marginBottom: remCalc(48),
        },
);

const StyledProgramTitle = styled('span')(() => ({
    margin: 0,
    fontFamily: font.family.body,
    fontSize: font.size.xs,
    lineHeight: remCalc(sizeMap.xs * 1.2),
    color: palette('grey', 'dark'),

    [mediaQueries.medium]: {
        fontSize: font.size.s,
        fontWeight: font.weight.medium,
        lineHeight: remCalc(sizeMap.s * 1.2),
        color: palette('white'),
    },

    [mediaQueries.large]: {
        fontSize: font.size.s,
        lineHeight: remCalc(sizeMap.s * 1.2),
    },
}));

const SubTitleBeginsRules = {
    margin: 0,
    fontFamily: font.family.body,
    fontSize: font.size.xs,
    fontWeight: font.weight.thin,
    lineHeight: font.lineHeight.xs,
    color: palette('grey', 'dark'),

    [mediaQueries.large]: {
        fontSize: font.size.s,
        lineHeight: font.lineHeight.s,
    },
};

const StyledProgramSubTitle = styled('div')(() => SubTitleBeginsRules);
const StyledProgramBegins = styled('div')(() => SubTitleBeginsRules);

export {
    stylingProgramLink,
    StyledProgram,
    StyledProgramButton,
    StyledProgramFigure,
    StyledProgramWrapper,
    StyledProgramHeader,
    StyledProgramDate,
    StyledProgramHour,
    StyledProgramPlay,
    StyledProgramLive,
    StyledProgramDescription,
    StyledProgramTitlesWrapper,
    StyledProgramTitle,
    StyledProgramSubTitle,
    StyledProgramBegins,
};
